import React, { useState, useEffect } from 'react';
import { FileText, ExternalLink, Users, Shield, CheckCircle, AlertCircle, Copy, Download, Calendar, Building2, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const API_URL = (window.__BACKEND_URL__ || process.env.REACT_APP_BACKEND_URL) || 'http://localhost:8001';

const DeclarationsFormalites = () => {
  const [agents, setAgents] = useState([]);
  const [company, setCompany] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showDPAEModal, setShowDPAEModal] = useState(false);
  const [dpaeData, setDpaeData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Charger les agents
      const agentsResponse = await axios.get(`${API_URL}/api/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgents(agentsResponse.data);

      // Charger les infos de la société
      const userResponse = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Note: On devrait avoir une route pour récupérer les infos complètes de la société
      // Pour l'instant on utilise les données du user
      setCompany(userResponse.data);
      
    } catch (error) {
      console.error('Erreur chargement données:', error);
    }
  };

  const handlePrepareDPAE = (agent) => {
    setSelectedAgent(agent);
    
    // Pré-remplir les données DPAE
    setDpaeData({
      // Employeur
      siret: company?.companyId || '',
      raisonSociale: company?.companyName || '',
      adresseEmployeur: '',
      
      // Salarié
      nom: agent.name?.split(' ')[1] || agent.name,
      prenom: agent.name?.split(' ')[0] || '',
      numeroSecuriteSociale: '',
      dateNaissance: '',
      lieuNaissance: '',
      nationalite: 'Française',
      adresseSalarie: agent.address || '',
      
      // Emploi
      dateEmbauche: '',
      heureEmbauche: '08:00',
      typeContrat: 'CDI',
      dureeContrat: '',
      emploi: agent.role || 'Agent de sécurité',
      qualification: '',
      conventionCollective: '1351 - Prévention et sécurité'
    });
    
    setShowDPAEModal(true);
  };

  const handleCopyDPAE = () => {
    const text = `
DPAE - Déclaration Préalable À l'Embauche
==========================================

EMPLOYEUR:
- SIRET: ${dpaeData.siret}
- Raison sociale: ${dpaeData.raisonSociale}
- Adresse: ${dpaeData.adresseEmployeur}

SALARIÉ:
- Nom: ${dpaeData.nom}
- Prénom: ${dpaeData.prenom}
- N° Sécurité Sociale: ${dpaeData.numeroSecuriteSociale}
- Date de naissance: ${dpaeData.dateNaissance}
- Lieu de naissance: ${dpaeData.lieuNaissance}
- Nationalité: ${dpaeData.nationalite}
- Adresse: ${dpaeData.adresseSalarie}

EMPLOI:
- Date d'embauche: ${dpaeData.dateEmbauche}
- Heure d'embauche: ${dpaeData.heureEmbauche}
- Type de contrat: ${dpaeData.typeContrat}
- Emploi: ${dpaeData.emploi}
- Convention collective: ${dpaeData.conventionCollective}
    `.trim();
    
    navigator.clipboard.writeText(text);
    alert('✅ Données copiées ! Collez-les dans le formulaire URSSAF');
  };

  const handleDownloadCERFA = () => {
    // Télécharger le CERFA vierge
    window.open('https://customer-assets.emergentagent.com/job_secuguard-7/artifacts/x2nb9e1c_14738-01.pdf', '_blank');
  };

  const handleSendEmail = () => {
    const subject = encodeURIComponent('DPAE - Déclaration Préalable À l\'Embauche');
    const body = encodeURIComponent(`
Bonjour,

Veuillez trouver ci-dessous les informations pour la DPAE :

ÉTABLISSEMENT EMPLOYEUR:
- N° SIRET: ${dpaeData.siret}
- Dénomination: ${dpaeData.raisonSociale}
- Adresse: ${dpaeData.adresseEmployeur}

FUTUR SALARIÉ:
- Nom de famille: ${dpaeData.nom}
- Prénom(s): ${dpaeData.prenom}
- N° Sécurité Sociale: ${dpaeData.numeroSecuriteSociale}
- Date de naissance: ${dpaeData.dateNaissance}
- Lieu de naissance: ${dpaeData.lieuNaissance}
- Adresse: ${dpaeData.adresseSalarie}

L'EMBAUCHE ET L'EMPLOI:
- Date prévisible d'embauche: ${dpaeData.dateEmbauche}
- Heure prévisible d'embauche: ${dpaeData.heureEmbauche}
- Type de contrat: ${dpaeData.typeContrat}
- Emploi: ${dpaeData.emploi}

Cordialement,
${dpaeData.raisonSociale}
    `);
    
    window.location.href = `mailto:sfe@urssaf.fr?subject=${subject}&body=${body}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3 mb-2">
            <FileText className="h-8 w-8 text-indigo-600" />
            Déclarations & Formalités Obligatoires
          </h1>
          <p className="text-gray-600">
            Gérez vos démarches administratives : DPAE, CNAPS, et autres formalités du secteur de la sécurité privée
          </p>
        </div>

        {/* Quick Actions Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* DPAE / DUE */}
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-lg">
                <Users className="h-8 w-8" />
              </div>
              <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-semibold">
                OBLIGATOIRE
              </span>
            </div>
            
            <h2 className="text-2xl font-bold mb-2">DPAE - Déclaration d'Embauche</h2>
            <p className="text-blue-100 mb-4 text-sm">
              Déclaration Préalable À l'Embauche (anciennement DUE). À effectuer avant toute embauche auprès de l'URSSAF.
            </p>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-blue-100">
                <CheckCircle className="h-4 w-4" />
                Obligatoire avant l'embauche
              </div>
              <div className="flex items-center gap-2 text-sm text-blue-100">
                <CheckCircle className="h-4 w-4" />
                Déclaration en ligne sur URSSAF
              </div>
              <div className="flex items-center gap-2 text-sm text-blue-100">
                <CheckCircle className="h-4 w-4" />
                Pré-remplissage automatique
              </div>
            </div>
            
            <div className="flex gap-3">
              <a
                href="https://www.due.urssaf.fr/declarant/formulaireDueLibre.jsf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-white text-blue-600 px-4 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink className="h-5 w-5" />
                Accéder à URSSAF
              </a>
            </div>
          </div>

          {/* CNAPS */}
          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg p-6 text-white">
            <div className="flex items-start justify-between mb-4">
              <div className="p-3 bg-white bg-opacity-20 rounded-lg">
                <Shield className="h-8 w-8" />
              </div>
              <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-xs font-semibold">
                SÉCURITÉ PRIVÉE
              </span>
            </div>
            
            <h2 className="text-2xl font-bold mb-2">Téléservices CNAPS</h2>
            <p className="text-indigo-100 mb-4 text-sm">
              Conseil National des Activités Privées de Sécurité. Déclarations de salariés, autorisations, cartes professionnelles.
            </p>
            
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-indigo-100">
                <CheckCircle className="h-4 w-4" />
                Déclaration des salariés
              </div>
              <div className="flex items-center gap-2 text-sm text-indigo-100">
                <CheckCircle className="h-4 w-4" />
                Suivi des cartes professionnelles
              </div>
              <div className="flex items-center gap-2 text-sm text-indigo-100">
                <CheckCircle className="h-4 w-4" />
                Autorisations d'exercice
              </div>
            </div>
            
            <a
              href="https://teleservices-cnaps.interieur.gouv.fr/teleservices/ihm/#/home"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-white text-indigo-600 px-4 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition-colors flex items-center justify-center gap-2"
            >
              <ExternalLink className="h-5 w-5" />
              Accéder au CNAPS
            </a>
          </div>
        </div>

        {/* Agents List for DPAE */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-6">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-blue-600" />
              Préparer une DPAE pour un agent
            </h2>
            <p className="text-gray-600 text-sm mt-1">
              Sélectionnez un agent pour pré-remplir automatiquement sa déclaration d'embauche
            </p>
          </div>

          {agents.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucun agent enregistré</p>
              <p className="text-gray-400 text-sm mt-2">Ajoutez des agents depuis le menu "Agents"</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {agents.map((agent) => (
                <div key={agent.id} className="p-6 hover:bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <Users className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{agent.name}</h3>
                      <p className="text-sm text-gray-600">{agent.role || 'Agent de sécurité'}</p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {agent.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {agent.phone}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => handlePrepareDPAE(agent)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    <FileText className="h-5 w-5" />
                    Préparer DPAE
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-900 mb-1">
                  Délai DPAE
                </h3>
                <p className="text-sm text-yellow-800">
                  La DPAE doit être effectuée au plus tôt 8 jours avant la date d'embauche et au plus tard avant la prise de fonction du salarié.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
            <div className="flex items-start">
              <Shield className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-semibold text-blue-900 mb-1">
                  Carte professionnelle CNAPS
                </h3>
                <p className="text-sm text-blue-800">
                  Tout agent de sécurité doit posséder une carte professionnelle en cours de validité. La demande se fait via les téléservices CNAPS.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal DPAE */}
      {showDPAEModal && selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full my-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="h-6 w-6 text-blue-600" />
                DPAE - {selectedAgent.name}
              </h2>
              <button
                onClick={() => setShowDPAEModal(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ✕
              </button>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded mb-6">
              <p className="text-sm text-blue-900">
                📋 <strong>Instructions :</strong> Complétez les informations ci-dessous, puis copiez-les pour les coller dans le formulaire URSSAF en ligne.
              </p>
            </div>

            <div className="space-y-6 max-h-96 overflow-y-auto p-2">
              {/* Employeur */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-gray-600" />
                  Informations Employeur
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SIRET</label>
                    <input
                      type="text"
                      value={dpaeData.siret}
                      onChange={(e) => setDpaeData({...dpaeData, siret: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Raison sociale</label>
                    <input
                      type="text"
                      value={dpaeData.raisonSociale}
                      onChange={(e) => setDpaeData({...dpaeData, raisonSociale: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse</label>
                    <input
                      type="text"
                      value={dpaeData.adresseEmployeur}
                      onChange={(e) => setDpaeData({...dpaeData, adresseEmployeur: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Salarié */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5 text-gray-600" />
                  Informations Salarié
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom</label>
                    <input
                      type="text"
                      value={dpaeData.nom}
                      onChange={(e) => setDpaeData({...dpaeData, nom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Prénom</label>
                    <input
                      type="text"
                      value={dpaeData.prenom}
                      onChange={(e) => setDpaeData({...dpaeData, prenom: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">N° Sécurité Sociale</label>
                    <input
                      type="text"
                      value={dpaeData.numeroSecuriteSociale}
                      onChange={(e) => setDpaeData({...dpaeData, numeroSecuriteSociale: e.target.value})}
                      placeholder="1 85 12 75 XXX XXX XX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label>
                    <input
                      type="date"
                      value={dpaeData.dateNaissance}
                      onChange={(e) => setDpaeData({...dpaeData, dateNaissance: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Lieu de naissance</label>
                    <input
                      type="text"
                      value={dpaeData.lieuNaissance}
                      onChange={(e) => setDpaeData({...dpaeData, lieuNaissance: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label>
                    <input
                      type="text"
                      value={dpaeData.nationalite}
                      onChange={(e) => setDpaeData({...dpaeData, nationalite: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Adresse du salarié</label>
                    <input
                      type="text"
                      value={dpaeData.adresseSalarie}
                      onChange={(e) => setDpaeData({...dpaeData, adresseSalarie: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Emploi */}
              <div>
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-gray-600" />
                  Informations Emploi
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date d'embauche</label>
                    <input
                      type="date"
                      value={dpaeData.dateEmbauche}
                      onChange={(e) => setDpaeData({...dpaeData, dateEmbauche: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Heure d'embauche</label>
                    <input
                      type="time"
                      value={dpaeData.heureEmbauche}
                      onChange={(e) => setDpaeData({...dpaeData, heureEmbauche: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                    <select
                      value={dpaeData.typeContrat}
                      onChange={(e) => setDpaeData({...dpaeData, typeContrat: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="CTT">CTT (Intérim)</option>
                      <option value="Saisonnier">Saisonnier</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Emploi</label>
                    <input
                      type="text"
                      value={dpaeData.emploi}
                      onChange={(e) => setDpaeData({...dpaeData, emploi: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Convention collective</label>
                    <input
                      type="text"
                      value={dpaeData.conventionCollective}
                      onChange={(e) => setDpaeData({...dpaeData, conventionCollective: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCopyDPAE}
                className="flex-1 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <Copy className="h-5 w-5" />
                Copier les données
              </button>
              <button
                onClick={handleDownloadCERFA}
                className="flex-1 bg-purple-600 text-white px-4 py-3 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
              >
                <Download className="h-5 w-5" />
                CERFA PDF
              </button>
            </div>
            
            <div className="flex gap-3 mt-3">
              <a
                href="https://www.due.urssaf.fr/declarant/formulaireDueLibre.jsf"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
              >
                <ExternalLink className="h-5 w-5" />
                Ouvrir URSSAF
              </a>
              <button
                onClick={handleSendEmail}
                className="flex-1 bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700 flex items-center justify-center gap-2"
              >
                <Mail className="h-5 w-5" />
                Email URSSAF
              </button>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded mt-4">
              <p className="text-sm text-yellow-900">
                ⚠️ <strong>En cas de problème de connexion au site URSSAF :</strong>
              </p>
              <ul className="text-sm text-yellow-800 mt-2 space-y-1 ml-4 list-disc">
                <li>Téléchargez le CERFA PDF et remplissez-le manuellement</li>
                <li>Ou envoyez directement par email à <strong>sfe@urssaf.fr</strong></li>
                <li>Cela évitera les amendes pour déclaration tardive</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeclarationsFormalites;
